/**
 * useAdsbReplayLayer — renders historical ADS-B aircraft snapshots on a CesiumJS
 * viewer during flight replay.
 *
 * Fetches ADS-B snapshots captured around a given track's flight, then on each
 * animation frame finds the snapshot closest to the current playback time and
 * renders those aircraft as oriented airplane billboard entities. The result is
 * a "traffic replay" showing nearby manned aircraft at the time of the drone
 * flight.
 *
 * Aircraft are color-coded by altitude band (same scheme as the live ADS-B layer)
 * and rendered semi-transparent (0.7 alpha) to visually distinguish them from
 * live traffic.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Viewer as CesiumViewer,
  CustomDataSource,
  Cartesian3,
  Cartesian2,
  Color,
  NearFarScalar,
  VerticalOrigin,
  HorizontalOrigin,
  LabelStyle,
  HeightReference,
  Math as CesiumMath,
  ConstantProperty,
} from 'cesium';
import { api } from '../services/api';
import { useMapStore } from '../store/mapStore';

const DATASOURCE_NAME = 'adsb-replay';

// ── Airplane icon SVG ──────────────────────────────────────────────────
// Top-down silhouette in white with a subtle dark outline for readability.
// Tinted via Cesium's billboard color property.
const AIRPLANE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="48" height="48">
  <g fill="white" stroke="rgba(0,0,0,0.5)" stroke-width="0.8" stroke-linejoin="round">
    <path d="M24 3 L22 14 L5 21 L5 24 L22 20.5 L21.5 34 L16 38 L16 40 L24 38 L32 40 L32 38 L26.5 34 L26 20.5 L43 24 L43 21 L26 14 Z"/>
  </g>
</svg>`;
const AIRPLANE_DATA_URI = `data:image/svg+xml;base64,${btoa(AIRPLANE_SVG)}`;

// ── Types ──────────────────────────────────────────────────────────────

interface ADSBSnapshotAircraft {
  hex: string;
  flight?: string;
  r?: string;       // registration
  t?: string;       // aircraft type
  lat: number;
  lon: number;
  alt_baro?: number | 'ground';
  alt_geom?: number;
  gs?: number;       // ground speed (knots)
  track?: number;    // heading (degrees)
  baro_rate?: number; // vertical rate (ft/min)
  squawk?: string;
  category?: string;
}

interface ADSBSnapshot {
  id: string;
  capturedAt: string; // ISO 8601
  centerLat: number;
  centerLon: number;
  radiusNm: number;
  aircraft: ADSBSnapshotAircraft[];
  triggerTrackIds: string[];
}

// ── Altitude color ramp ────────────────────────────────────────────────
// Matches the live ADS-B layer (useOpenSkyLayer) for visual consistency.

function getAltitudeColor(altFt: number | null, onGround: boolean): Color {
  if (onGround || altFt === 0) return Color.fromCssColorString('#9ca3af'); // gray-400
  if (altFt == null) return Color.fromCssColorString('#5fd5ff');
  if (altFt < 10_000) return Color.fromCssColorString('#5fd5ff');  // sky blue (low)
  if (altFt < 25_000) return Color.fromCssColorString('#ffb547');  // teal (mid)
  return Color.fromCssColorString('#a78bfa');                       // violet (en-route)
}

// ── Binary search for closest snapshot ─────────────────────────────────

/**
 * Find the snapshot whose capturedAt timestamp is closest to `targetMs`.
 * Assumes `snapshots` is sorted by capturedAt ascending.
 */
function findClosestSnapshot(
  snapshots: ADSBSnapshot[],
  targetMs: number,
): ADSBSnapshot | null {
  if (snapshots.length === 0) return null;
  if (snapshots.length === 1) return snapshots[0];

  let lo = 0;
  let hi = snapshots.length - 1;

  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    const midTime = new Date(snapshots[mid].capturedAt).getTime();

    if (midTime < targetMs) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }

  // `lo` is the first snapshot >= targetMs. Compare it with lo-1 to find the closest.
  if (lo === 0) return snapshots[0];

  const prev = new Date(snapshots[lo - 1].capturedAt).getTime();
  const curr = new Date(snapshots[lo].capturedAt).getTime();

  return Math.abs(prev - targetMs) <= Math.abs(curr - targetMs)
    ? snapshots[lo - 1]
    : snapshots[lo];
}

// ── Hook ───────────────────────────────────────────────────────────────

export function useAdsbReplayLayer(
  viewer: CesiumViewer | null,
  trackId: string | null,
  visible: boolean,
): void {
  const dsRef = useRef<CustomDataSource | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastSnapshotIdRef = useRef<string | null>(null);

  // Read timeRange from store (for the query bounds)
  const timeRange = useMapStore((s) => s.timeRange);

  // ── Fetch ADS-B snapshots for the track's time window ──────────────
  const { data: snapshots } = useQuery<ADSBSnapshot[]>({
    queryKey: ['adsb-replay', trackId],
    queryFn: async () => {
      const params = new URLSearchParams({
        trackId: trackId!,
        startTime: timeRange!.start.toISOString(),
        endTime: timeRange!.end.toISOString(),
      });
      const response = await api.get<{ data: ADSBSnapshot[] }>(
        `/adsb-snapshots?${params.toString()}`,
      );
      // Sort by capturedAt ascending for binary search
      const sorted = (response.data.data ?? response.data as unknown as ADSBSnapshot[]).sort(
        (a, b) =>
          new Date(a.capturedAt).getTime() - new Date(b.capturedAt).getTime(),
      );
      return sorted;
    },
    enabled: !!trackId && visible && !!timeRange,
    staleTime: 5 * 60 * 1000,
  });

  // ── Ensure the CustomDataSource exists ─────────────────────────────
  const ensureDataSource = useCallback(
    (v: CesiumViewer): CustomDataSource => {
      let ds = dsRef.current;
      if (!ds) {
        ds = new CustomDataSource(DATASOURCE_NAME);
        dsRef.current = ds;
        v.dataSources.add(ds);
      }
      return ds;
    },
    [],
  );

  // ── Render a single snapshot's aircraft onto the data source ───────
  const renderSnapshot = useCallback(
    (ds: CustomDataSource, snapshot: ADSBSnapshot) => {
      const currentIds = new Set(snapshot.aircraft.map((ac) => ac.hex));

      // Remove stale entities no longer in this snapshot
      const existing = ds.entities.values.slice();
      for (const entity of existing) {
        if (!currentIds.has(entity.id)) {
          ds.entities.removeById(entity.id);
        }
      }

      // Upsert aircraft entities
      for (const ac of snapshot.aircraft) {
        const onGround = ac.alt_baro === 'ground';
        const altFt =
          ac.alt_baro === 'ground' || ac.alt_baro == null
            ? (ac.alt_geom ?? 0)
            : ac.alt_baro;
        const altM = (typeof altFt === 'number' ? altFt : 0) * 0.3048;
        const baseColor = getAltitudeColor(
          typeof altFt === 'number' ? altFt : null,
          onGround,
        );
        // Semi-transparent to differentiate from live traffic
        const color = baseColor.withAlpha(0.7);
        const position = Cartesian3.fromDegrees(
          ac.lon,
          ac.lat,
          onGround ? 0 : altM,
        );
        const callsign = ac.flight?.trim() || ac.hex.toUpperCase();
        const rotation = ac.track != null ? -CesiumMath.toRadians(ac.track) : 0;

        const existingEntity = ds.entities.getById(ac.hex);
        if (existingEntity) {
          // Update existing entity in-place
          existingEntity.position = position as any;
          if (existingEntity.billboard) {
            existingEntity.billboard.color = color as any;
            existingEntity.billboard.rotation = new ConstantProperty(rotation) as any;
          }
          if (existingEntity.label) {
            (existingEntity.label.text as any) = callsign;
          }
        } else {
          // Create new entity
          ds.entities.add({
            id: ac.hex,
            position,
            billboard: {
              image: AIRPLANE_DATA_URI,
              width: 30,
              height: 30,
              color,
              rotation,
              alignedAxis: Cartesian3.UNIT_Z,
              verticalOrigin: VerticalOrigin.CENTER,
              horizontalOrigin: HorizontalOrigin.CENTER,
              scaleByDistance: new NearFarScalar(5_000, 1.3, 400_000, 0.45),
              heightReference: onGround
                ? HeightReference.CLAMP_TO_GROUND
                : HeightReference.NONE,
            },
            label: {
              text: callsign,
              font: '600 11px "Inter", "DM Sans", system-ui, sans-serif',
              fillColor: Color.WHITE,
              style: LabelStyle.FILL,
              showBackground: false,
              pixelOffset: new Cartesian2(0, -22),
              verticalOrigin: VerticalOrigin.BOTTOM,
              horizontalOrigin: HorizontalOrigin.CENTER,
              scaleByDistance: new NearFarScalar(5_000, 0.9, 400_000, 0.3),
            },
          });
        }
      }
    },
    [],
  );

  // ── Animation loop — sync entities to playback time ────────────────
  useEffect(() => {
    if (!viewer || viewer.isDestroyed() || !visible || !snapshots?.length) {
      return;
    }

    const ds = ensureDataSource(viewer);
    ds.show = true;

    const animate = () => {
      if (!viewer || viewer.isDestroyed() || !dsRef.current) return;

      const { playbackTime } = useMapStore.getState();
      if (!playbackTime) {
        rafRef.current = requestAnimationFrame(animate);
        return;
      }

      const targetMs = playbackTime.getTime();
      const closest = findClosestSnapshot(snapshots, targetMs);

      if (closest && closest.id !== lastSnapshotIdRef.current) {
        // Snapshot changed — re-render aircraft
        lastSnapshotIdRef.current = closest.id;
        renderSnapshot(ds, closest);
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      lastSnapshotIdRef.current = null;
    };
  }, [viewer, visible, snapshots, ensureDataSource, renderSnapshot]);

  // ── Hide data source when layer is toggled off ─────────────────────
  useEffect(() => {
    if (dsRef.current) {
      dsRef.current.show = visible;
    }
  }, [visible]);

  // ── Cleanup on unmount or when trackId becomes null ────────────────
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (dsRef.current && viewer && !viewer.isDestroyed()) {
        viewer.dataSources.remove(dsRef.current, true);
        dsRef.current = null;
      }
      lastSnapshotIdRef.current = null;
    };
  }, [viewer, trackId]);
}
